import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { money2 } from "@/lib/format";
import { ErrorState } from "@/components/shared";
import { InflowProductPicker } from "@/components/InflowProductPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Package, Send, Check, AlertTriangle,
  CheckCircle2, HardHat, Settings2, Circle, CircleDot, Truck, FileDown, Share2, Copy,
  Upload, Paperclip, Download, FileText, Pencil, NotebookPen,
} from "lucide-react";
import { toast } from "sonner";
import { PdfViewer } from "@/components/PdfViewer";
import { cn } from "@/lib/utils";

const uid = () => "li_" + Math.random().toString(36).slice(2, 12);
const ALL_STAGES = [
  { key: "rough_in", label: "Rough In", hint: "Inwalls and Mixers & Sinks" },
  { key: "tiling", label: "Tiles", hint: "Tiles & Mosaic" },
  { key: "tapware", label: "Plumbing Fixtures", hint: "Showers, Toilets, Bathtub" },
  { key: "flooring", label: "Flooring", hint: "Hybrid, Engineered" },
];

function StageCard({ jobId, stage, data, inflowEnabled, pushRecord, isCurrent, onChanged, canDelete }) {
  const qc = useQueryClient();
  const isMisc = stage === "misc" || stage.startsWith("misc_");
  const [items, setItems] = useState(data.items || []);
  const [gstInclusive, setGstInclusive] = useState(!!data.gst_inclusive);
  const [timeline, setTimeline] = useState(data.delivery_timeline || "");
  const [tentative, setTentative] = useState(!!data.tentative);
  const [renaming, setRenaming] = useState(false);
  const [titleVal, setTitleVal] = useState(data.label || "");
  const [saving, setSaving] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => { setItems(data.items || []); setGstInclusive(!!data.gst_inclusive); setTimeline(data.delivery_timeline || ""); setTentative(!!data.tentative); setTitleVal(data.label || ""); }, [data]);

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { id: uid(), name: "", qty: 1, unit_price: 0, notes: "", inflow_product_id: null, inflow_product_name: null, sku: null }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const rate = 10; // Standard Australian GST — fixed
  const gross = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const taxAmount = gstInclusive ? gross - gross / (1 + rate / 100) : gross * rate / 100;
  const total = gstInclusive ? gross : gross + taxAmount;
  const done = data.status === "done";
  const pushed = !!pushRecord;

  const cleanItems = () => items
    .filter((it) => (it.name || it.inflow_product_name || "").trim())
    .map((it) => ({
      id: it.id || uid(), name: (it.inflow_product_name || it.name || "").trim(),
      qty: Number(it.qty) || 0, unit_price: Number(it.unit_price) || 0, notes: it.notes || null,
      sku: it.sku || null, inflow_product_id: it.inflow_product_id || null, inflow_product_name: it.inflow_product_name || null,
    }));

  const copyNum = async (n) => { try { await navigator.clipboard.writeText(n); toast.success("Invoice number copied"); } catch { toast.error("Copy failed"); } };

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/builder-jobs/${jobId}/stages/${stage}`, { tax_rate: 10, gst_inclusive: gstInclusive, delivery_timeline: timeline, tentative, items: cleanItems() });
      toast.success(`${data.label} selections saved`);
      onChanged?.();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); return false; }
    finally { setSaving(false); }
  };

  const toggleDone = async () => {
    try {
      await client.put(`/builder-jobs/${jobId}/stages/${stage}`, { status: done ? "active" : "done" });
      toast.success(done ? `${data.label} reopened` : `${data.label} marked done`);
      onChanged?.();
    } catch { toast.error("Could not update stage"); }
  };

  const openPush = async () => {
    setPushResult(null); setPreview(null); setPreviewing(true); setPushOpen(true);
    const ok = await save();
    if (!ok) { setPushOpen(false); setPreviewing(false); return; }
    try {
      const { data: p } = await client.get(`/builder-jobs/${jobId}/stages/${stage}/inflow/preview`);
      setPreview(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build preview"); setPushOpen(false); }
    finally { setPreviewing(false); }
  };

  const confirmPush = async () => {
    setPushing(true);
    try {
      const { data: r } = await client.post(`/builder-jobs/${jobId}/stages/${stage}/inflow/push`);
      setPushResult(r);
      qc.invalidateQueries({ queryKey: ["builder-job", jobId] });
      const num = r.order_number;
      toast.success(
        r.status === "already_created" ? `Already in inFlow · ${num}` : `Invoice ${num} pushed to inFlow`,
        num ? { description: `Invoice number ${num}`, action: { label: "Copy", onClick: () => copyNum(num) } } : undefined
      );
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
    finally { setPushing(false); }
  };

  const updatePush = async () => {
    setUpdating(true);
    try {
      const ok = await save();
      if (!ok) { setUpdating(false); return; }
      const { data: r } = await client.post(`/builder-jobs/${jobId}/stages/${stage}/inflow/update`);
      qc.invalidateQueries({ queryKey: ["builder-job", jobId] });
      toast.success(`Invoice ${r.order_number} updated in inFlow`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setUpdating(false); }
  };

  const anyUnmapped = inflowEnabled && items.length > 0 && items.some((it) => !it.inflow_product_id);

  const saveTitle = async () => {
    const t = titleVal.trim();
    if (!t) { toast.error("Enter a title"); return; }
    try {
      await client.put(`/builder-jobs/${jobId}/stages/${stage}`, { title: t });
      toast.success("Renamed"); setRenaming(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
  };
  const delMisc = async () => {
    if (!window.confirm("Remove this miscellaneous entry? This can't be undone.")) return;
    try {
      await client.delete(`/builder-jobs/${jobId}/misc/${stage}`);
      toast.success("Entry removed"); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div data-testid={`stage-card-${stage}`} className={cn("rounded-2xl border bg-white shadow-[0_2px_12px_rgba(35,52,90,0.04)]", done ? "border-[#A7F3D0]" : isCurrent ? "border-primary/40" : "border-slate-100")}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
        <div className="flex items-center gap-3">
          <div className={cn("grid h-10 w-10 place-items-center rounded-xl", done ? "bg-[#D1FAE5] text-[#059669]" : "bg-primary/10 text-primary")}>
            {done ? <CheckCircle2 className="h-5 w-5" /> : <Package className="h-5 w-5" strokeWidth={1.75} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              {renaming ? (
                <div className="flex items-center gap-1.5">
                  <Input value={titleVal} onChange={(e) => setTitleVal(e.target.value)} className="h-8 w-44" data-testid={`misc-rename-input-${stage}`} autoFocus onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); }} />
                  <Button size="sm" onClick={saveTitle} data-testid={`misc-rename-save-${stage}`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setRenaming(false); setTitleVal(data.label || ""); }}>Cancel</Button>
                </div>
              ) : (
                <h3 className="font-display text-base font-bold text-ink">{data.label}</h3>
              )}
              {isCurrent && !done && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">CURRENT</span>}
              {pushed && <span data-testid={`stage-pushed-${stage}`} className="flex items-center gap-1 rounded-full bg-[#D1FAE5] px-2 py-0.5 text-[10px] font-semibold text-[#065F46]"><CheckCircle2 className="h-3 w-3" />{pushRecord.order_number}</span>}
              {isMisc && !renaming && <button onClick={() => setRenaming(true)} data-testid={`misc-rename-${stage}`} title="Rename" className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-ink"><Pencil className="h-3.5 w-3.5" /></button>}
              {canDelete && !renaming && <button onClick={delMisc} data-testid={`misc-delete-${stage}`} title="Remove entry" className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
            <p className="text-xs text-muted-foreground">{data.item_count || 0} selection(s) · {money2(total)}</p>
          </div>
        </div>
        <Button variant={done ? "outline" : "outline"} size="sm" onClick={toggleDone} data-testid={`stage-done-toggle-${stage}`} className={cn("gap-1.5", done ? "text-success hover:text-success" : "text-muted-foreground")}>
          {done ? <><CircleDot className="h-3.5 w-3.5" />Reopen</> : <><Circle className="h-3.5 w-3.5" />Mark done</>}
        </Button>
      </div>

      <div className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Selections</span>
          <Button onClick={addItem} size="sm" variant="outline" data-testid={`stage-add-item-${stage}`} className="gap-1.5"><Plus className="h-4 w-4" />Add item</Button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-border py-8 text-center">
            <Package className="mb-2 h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No selections yet.</p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-2">
            {items.map((it, idx) => (
              <div key={it.id || idx} data-testid={`stage-${stage}-item-${idx}`} className="flex flex-col gap-3 rounded-xl border border-border p-3 md:grid md:grid-cols-12 md:items-center md:gap-2 md:p-2.5">
                <div className="md:col-span-4">
                  {inflowEnabled
                    ? <InflowProductPicker item={it} onSelect={(patch) => setItem(idx, patch)} />
                    : <Input value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })} placeholder="Product / material" data-testid={`stage-${stage}-name-${idx}`} className="h-11 text-base md:h-10 md:text-sm" />}
                </div>
                <div className="grid grid-cols-2 gap-3 md:contents">
                  <div className="md:col-span-1">
                    <Label className="mb-1 block text-[11px] text-muted-foreground md:hidden">Qty</Label>
                    <Input type="number" min="0" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} className="h-11 text-right text-base md:h-10 md:text-sm" data-testid={`stage-${stage}-qty-${idx}`} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="mb-1 block text-[11px] text-muted-foreground md:hidden">Unit price</Label>
                    <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} className="h-11 text-right text-base md:h-10 md:text-sm" data-testid={`stage-${stage}-price-${idx}`} />
                  </div>
                </div>
                <div className="md:col-span-3">
                  <Label className="mb-1 block text-[11px] text-muted-foreground md:hidden">Notes</Label>
                  <Input value={it.notes || ""} onChange={(e) => setItem(idx, { notes: e.target.value })} placeholder="Notes" data-testid={`stage-${stage}-notes-${idx}`} className="h-11 text-base md:h-10 md:text-sm" />
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 md:col-span-2 md:justify-end md:gap-1 md:border-0 md:pt-0">
                  <span className="text-xs text-muted-foreground md:hidden">Line total</span>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-bold text-ink">{money2((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</span>
                    <button onClick={() => removeItem(idx)} data-testid={`stage-${stage}-remove-${idx}`} className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error md:h-7 md:w-7"><Trash2 className="h-4 w-4 md:h-3.5 md:w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {anyUnmapped && <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#D97706]/10 px-3 py-2 text-xs font-medium text-warning"><AlertTriangle className="h-4 w-4" />Link every item to an inFlow product before pushing.</div>}

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`stage-${stage}-timeline-row`}>
          <Truck className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Label className="shrink-0 text-xs text-muted-foreground">Indicative delivery date</Label>
          <Input type="date" value={/^\d{4}-\d{2}-\d{2}$/.test(timeline) ? timeline : ""} onChange={(e) => setTimeline(e.target.value)} data-testid={`stage-${stage}-timeline`} className="h-8 w-[170px]" />
          {timeline && !/^\d{4}-\d{2}-\d{2}$/.test(timeline) && <span className="text-[11px] text-muted-foreground">Was: {timeline} — pick a date to replace</span>}
          <button type="button" onClick={() => setTentative((t) => !t)} data-testid={`stage-${stage}-tentative`}
            title="When on, the builder portal shows 'Tentative — to be confirmed' instead of this date"
            className={cn("rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors", tentative ? "border-[#D97706]/40 bg-[#D97706]/10 text-warning" : "border-border text-muted-foreground hover:text-ink")}>
            {tentative ? "Tentative ✓" : "Mark tentative"}
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <div className="inline-flex rounded-lg border border-border bg-white p-0.5" data-testid={`stage-${stage}-gst-toggle`}>
              <button type="button" onClick={() => setGstInclusive(false)} data-testid={`stage-${stage}-gst-excl`} className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors", !gstInclusive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-ink")}>Excl. GST</button>
              <button type="button" onClick={() => setGstInclusive(true)} data-testid={`stage-${stage}-gst-incl`} className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors", gstInclusive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-ink")}>Incl. GST</button>
            </div>
            <span className="text-xs">GST 10% <span className="font-semibold text-ink" data-testid={`stage-${stage}-gst-amount`}>{money2(taxAmount)}</span>{gstInclusive ? " (incl.)" : ""}</span>
            <span>Total{gstInclusive ? " (inc GST)" : ""} <span className="font-display font-bold text-ink" data-testid={`stage-${stage}-total`}>{money2(total)}</span></span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center [&_button]:w-full sm:[&_button]:w-auto">
            {inflowEnabled && !pushed && (
              <Button onClick={openPush} disabled={saving || items.length === 0} variant="outline" data-testid={`stage-push-${stage}`} className="gap-2"><Send className="h-4 w-4" />Push invoice</Button>
            )}
            {inflowEnabled && pushed && (
              <Button onClick={updatePush} disabled={saving || updating || items.length === 0 || anyUnmapped} variant="outline" data-testid={`stage-update-${stage}`} className="gap-2">{updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Update invoice</Button>
            )}
            <Button onClick={save} disabled={saving} data-testid={`stage-save-${stage}`} className="gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save</Button>
          </div>
        </div>
      </div>

      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid={`stage-push-dialog-${stage}`} className="max-w-lg gap-0 overflow-hidden p-0">
          <DialogHeader className="space-y-1 border-b border-border p-5 text-left">
            <DialogTitle className="font-display text-lg sm:text-xl">Push {data.label} invoice to inFlow</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">Creates an invoice in inFlow for this stage. This deducts stock and syncs to your accounting (Xero) straight away.</DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto p-5">
            {previewing ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Building preview…</div>
            ) : pushResult ? (
              <div className="py-6 text-center" data-testid={`stage-push-success-${stage}`}>
                <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-[#D1FAE5]"><CheckCircle2 className="h-8 w-8 text-[#059669]" /></div>
                <p className="font-display text-base font-bold text-ink">{pushResult.status === "already_created" ? "Already in inFlow" : "Invoice created in inFlow"}</p>
                <p className="mt-1 text-sm text-muted-foreground">Order <span className="font-mono font-semibold text-ink">{pushResult.order_number}</span></p>
                {pushResult.inflow_customer_name && <p className="text-xs text-muted-foreground">for {pushResult.inflow_customer_name}</p>}
              </div>
            ) : preview ? (
              preview.already_pushed ? (
                <div className="flex items-center gap-2 rounded-xl bg-accent px-3 py-3 text-sm text-accent-foreground"><Check className="h-4 w-4 shrink-0" />Already pushed as <span className="font-mono font-semibold">{preview.already_pushed.order_number}</span>.</div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><HardHat className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Builder</div>
                      <div className="truncate font-semibold text-ink">{preview.customer.display_name}</div>
                      <div className="text-xs text-muted-foreground">{preview.customer.will_create ? "Will be created in inFlow" : preview.customer.matched ? "Matched an existing inFlow customer" : "Will match or create on push"}</div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-border">
                    <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{preview.lines.length} line item(s)</span>
                      <span className="text-xs font-semibold text-ink" data-testid={`stage-push-total-${stage}`}>{money2(preview.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0))}</span>
                    </div>
                    <div className="max-h-56 divide-y divide-border overflow-y-auto">
                      {preview.lines.map((l, i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 text-sm">
                          {l.mapped ? <Check className="h-4 w-4 shrink-0 text-success" /> : <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />}
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-ink">{l.inflow_product_name || l.name || "(unnamed)"}</div>
                            {l.sku && <div className="truncate font-mono text-[10px] text-muted-foreground">{l.sku}</div>}
                          </div>
                          <span className="shrink-0 text-xs font-medium text-muted-foreground">{l.qty} × {money2(l.unit_price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {preview.gst_inclusive && <p className="rounded-xl bg-muted/60 px-3 py-2.5 text-xs text-muted-foreground" data-testid={`stage-${stage}-gst-note`}>Prices are sent to inFlow <b>excluding GST</b>; inFlow adds {preview.tax_rate}% GST so the total matches your GST-inclusive pricing.</p>}
                  {!preview.all_mapped && <div className="flex items-center gap-2 rounded-xl bg-[#D97706]/10 px-3 py-2.5 text-xs font-medium text-warning"><AlertTriangle className="h-4 w-4 shrink-0" />{preview.unmapped_count} line(s) aren't linked to an inFlow product. Link them before pushing.</div>}
                </div>
              )
            ) : null}
          </div>

          <DialogFooter className="flex-col-reverse gap-2 border-t border-border p-4 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setPushOpen(false)} data-testid={`stage-push-close-${stage}`} className="w-full sm:w-auto">{pushResult ? "Done" : "Cancel"}</Button>
            {preview && !preview.already_pushed && !pushResult && (
              <Button onClick={confirmPush} disabled={pushing || !preview.all_mapped} data-testid={`stage-push-confirm-${stage}`} className="w-full gap-2 sm:w-auto">{pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Create inFlow invoice</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddMiscDialog({ jobId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const t = title.trim();
    if (!t) { toast.error("Enter a title"); return; }
    setSaving(true);
    try {
      await client.post(`/builder-jobs/${jobId}/misc`, { title: t });
      toast.success("Miscellaneous entry added");
      setOpen(false); setTitle(""); onAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="add-misc-btn" className="shrink-0 gap-1.5"><Plus className="h-3.5 w-3.5" />Add entry</Button>
      <DialogContent data-testid="add-misc-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">New miscellaneous entry</DialogTitle><DialogDescription>Give this ad-hoc invoice a short title, e.g. "Site variation" or "Extra tapware". It stays internal — never shown on PDFs or the builder portal.</DialogDescription></DialogHeader>
        <div className="py-1">
          <Label className="mb-1.5 block text-xs text-muted-foreground">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Site variation" data-testid="add-misc-title" autoFocus onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="add-misc-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditStagesDialog({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [stages, setStages] = useState(job.stages || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setStages(job.stages || []); }, [open, job.stages]);
  const toggle = (k) => setStages((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const submit = async () => {
    if (stages.length === 0) { toast.error("Select at least one stage"); return; }
    setSaving(true);
    try {
      await client.put(`/builder-jobs/${job.id}`, { stages });
      toast.success("Stages updated");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="edit-stages-btn" className="gap-1.5"><Settings2 className="h-3.5 w-3.5" />Edit stages</Button>
      <DialogContent data-testid="edit-stages-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Which stages apply?</DialogTitle><DialogDescription>Turn stages on or off for this job.</DialogDescription></DialogHeader>
        <div className="grid gap-2 py-1">
          {ALL_STAGES.map((s) => (
            <label key={s.key} data-testid={`edit-stage-toggle-${s.key}`} className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3 transition-colors hover:bg-muted/40">
              <Checkbox checked={stages.includes(s.key)} onCheckedChange={() => toggle(s.key)} className="mt-0.5" />
              <span><span className="block text-sm font-semibold text-ink">{s.label}</span><span className="block text-xs text-muted-foreground">{s.hint}</span></span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-stages-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ job, onChanged }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(!!job.share_enabled);
  const [token, setToken] = useState(job.share_token || null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setEnabled(!!job.share_enabled); setToken(job.share_token || null); } }, [open, job.share_enabled, job.share_token]);
  const shareUrl = token ? `${window.location.origin}/share/${token}` : "";
  const toggle = async (val) => {
    setSaving(true);
    try {
      const { data } = await client.post(`/builder-jobs/${job.id}/share`, { enabled: val });
      setEnabled(data.share_enabled); setToken(data.share_token);
      onChanged?.();
      toast.success(val ? "Public link enabled" : "Public link turned off");
    } catch { toast.error("Could not update sharing"); }
    finally { setSaving(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); } catch { toast.error("Copy failed — select and copy manually"); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="share-btn" className="gap-1.5"><Share2 className="h-3.5 w-3.5" />Share</Button>
      <DialogContent data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Share with builder</DialogTitle><DialogDescription>Turn on a public, read-only link the builder can open — selections and images only, no pricing.</DialogDescription></DialogHeader>
        <div className="flex items-center justify-between rounded-xl border border-border p-4">
          <div><div className="text-sm font-semibold text-ink">Public link</div><div className="text-xs text-muted-foreground">{enabled ? "Anyone with the link can view" : "Currently turned off"}</div></div>
          <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} data-testid="share-toggle" />
        </div>
        {enabled && token && (
          <div className="flex items-center gap-2">
            <Input readOnly value={shareUrl} onFocus={(e) => e.target.select()} data-testid="share-url" className="text-xs" />
            <Button onClick={copy} data-testid="copy-share-link" className="gap-1.5"><Copy className="h-4 w-4" />Copy</Button>
          </div>
        )}
        <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditJobDialog({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(job.title || "");
  const [address, setAddress] = useState(job.address || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setTitle(job.title || ""); setAddress(job.address || ""); } }, [open, job]);
  const submit = async () => {
    if (!title.trim()) { toast.error("Job title is required"); return; }
    setSaving(true);
    try {
      await client.put(`/builder-jobs/${job.id}`, { title: title.trim(), address: address.trim() });
      toast.success("Job updated");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update job"); }
    finally { setSaving(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="edit-job-btn" className="gap-1.5"><Pencil className="h-3.5 w-3.5" />Edit job</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-job-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Edit job</DialogTitle><DialogDescription>Fix the job title or site address if something was entered incorrectly.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-1">
            <div className="grid gap-1.5"><Label>Job title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-job-title" /></div>
            <div className="grid gap-1.5"><Label>Site address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. 42 Smith St, Blacktown NSW" data-testid="edit-job-address" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="edit-job-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function JobNotes({ job, onSaved }) {
  const [notes, setNotes] = useState(job.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotes(job.notes || ""); }, [job.id]);
  const dirty = (notes || "") !== (job.notes || "");
  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/builder-jobs/${job.id}`, { notes });
      toast.success("Notes saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save notes"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="job-notes-section">
      <div className="mb-2 flex items-center gap-2">
        <NotebookPen className="h-4 w-4 text-primary" />
        <h3 className="font-display text-base font-bold text-ink">Notes for builder / customer</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">One note for the whole job — it appears at the end of every PDF (builder and client copies).</p>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="e.g. Confirm all tile colours before we order — special-order items can't be returned. 30% deposit locks in pricing; balance due prior to delivery." data-testid="job-notes-input" />
      <div className="mt-3 flex justify-end">
        <Button onClick={save} disabled={saving || !dirty} size="sm" data-testid="job-notes-save" className="gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save notes</Button>
      </div>
    </div>
  );
}

function JobFiles({ jobId }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const files = useQuery({ queryKey: ["job-files", jobId], queryFn: async () => (await client.get(`/builder-jobs/${jobId}/files`)).data });

  const onPick = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    setUploading(true);
    try {
      for (const f of chosen) {
        const fd = new FormData();
        fd.append("file", f);
        await client.post(`/builder-jobs/${jobId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(chosen.length === 1 ? "File uploaded" : `${chosen.length} files uploaded`);
      qc.invalidateQueries({ queryKey: ["job-files", jobId] });
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const download = async (f) => {
    try {
      const res = await client.get(`/builder-jobs/${jobId}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file"); }
  };

  const remove = async (f) => {
    try {
      await client.delete(`/builder-jobs/${jobId}/files/${f.id}`);
      toast.success("File removed");
      qc.invalidateQueries({ queryKey: ["job-files", jobId] });
    } catch { toast.error("Could not remove file"); }
  };

  const list = files.data || [];
  return (
    <div className="mt-5 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="job-files-section">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-primary" />
          <h3 className="font-display text-base font-bold text-ink">Plans & files</h3>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Staff only</span>
        </div>
        <input ref={fileRef} type="file" multiple onChange={onPick} className="hidden" data-testid="job-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" variant="outline" data-testid="upload-file-btn" className="gap-1.5">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload
        </Button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Architecture plans and job documents. Visible to sales staff and admins only — never shown on the public builder link. Max 25 MB per file.</p>
      {files.isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-muted/50" />
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">No files yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {list.map((f) => (
            <div key={f.id} data-testid={`job-file-${f.id}`} className="flex items-center gap-3 py-2.5">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{f.original_filename}</div>
                <div className="text-xs text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} MB · {f.uploaded_by_name || "staff"}</div>
              </div>
              <button onClick={() => download(f)} data-testid={`download-file-${f.id}`} title="Download" className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-primary"><Download className="h-4 w-4" /></button>
              <button onClick={() => remove(f)} data-testid={`delete-file-${f.id}`} title="Remove" className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-error"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BuilderJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [pdfView, setPdfView] = useState(null); // { blob, name }
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data: job, isLoading, isError, refetch } = useQuery({ queryKey: ["builder-job", jobId], queryFn: async () => (await client.get(`/builder-jobs/${jobId}`)).data });

  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (isError) return <ErrorState onRetry={refetch} message="We couldn't load this job." />;
  if (!job) return <div className="py-20 text-center text-muted-foreground">Job not found.</div>;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["builder-job", jobId] });
    if (job?.builder_id) qc.invalidateQueries({ queryKey: ["builder", job.builder_id] });
    qc.invalidateQueries({ queryKey: ["builders"] });
  };
  const openPdf = async ({ stage, client: clientVariant } = {}) => {
    setDownloading(true);
    try {
      const params = {};
      if (stage) params.stage = stage;
      if (clientVariant) params.variant = "client";
      const res = await client.get(`/builder-jobs/${jobId}/pdf`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const base = (job.title || "job").replace(/[^a-z0-9]+/gi, "_");
      const stageSuffix = stage ? `-${job.stage_data?.[stage]?.label?.replace(/[^a-z0-9]+/gi, "_") || stage}` : "";
      const name = `TilesMate-${base}${stageSuffix}${clientVariant ? "-Client" : ""}.pdf`;
      setPdfView({ blob, name });
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };
  const downloadCurrentPdf = () => {
    if (!pdfView) return;
    const url = URL.createObjectURL(pdfView.blob);
    const a = document.createElement("a");
    a.href = url; a.download = pdfView.name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const deleteJob = async () => {
    setDeleting(true);
    try {
      await client.delete(`/builder-jobs/${jobId}`);
      qc.invalidateQueries({ queryKey: ["builder", job.builder_id] });
      qc.invalidateQueries({ queryKey: ["builders"] });
      toast.success("Job deleted");
      navigate(`/builders/${job.builder_id}`);
    }
    catch { toast.error("Could not delete job"); }
    finally { setDeleting(false); }
  };
  const stages = job.stages || [];
  const miscKeys = job.misc_keys || ["misc"];
  const currentStage = stages.find((s) => job.stage_data?.[s]?.status !== "done");

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate(`/builders/${job.builder_id}`)} data-testid="back-to-builder" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Back to {job.builder?.name || "builder"}
      </button>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary"><HardHat className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-ink" data-testid="job-title">{job.title}</h1>
            <p className="text-sm text-muted-foreground">{job.builder?.name}{job.address ? ` · ${job.address}` : ""}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center [&_button]:w-full sm:[&_button]:w-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={downloading} data-testid="download-pdf-btn" className="gap-1.5">{downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}View PDF</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Builder copy · with pricing</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => openPdf()} data-testid="pdf-full-summary">Full summary (all stages)</DropdownMenuItem>
              {stages.map((s) => (
                <DropdownMenuItem key={`b-${s}`} onClick={() => openPdf({ stage: s })} data-testid={`pdf-stage-${s}`}>{job.stage_data?.[s]?.label || s}</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Client copy · no pricing</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => openPdf({ client: true })} data-testid="pdf-client-full">Full summary (all stages)</DropdownMenuItem>
              {stages.map((s) => (
                <DropdownMenuItem key={`c-${s}`} onClick={() => openPdf({ stage: s, client: true })} data-testid={`pdf-client-stage-${s}`}>{job.stage_data?.[s]?.label || s}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <EditJobDialog job={job} onSaved={refresh} />
          <EditStagesDialog job={job} onSaved={refresh} />
          <ShareDialog job={job} onChanged={refresh} />
          <Button variant="outline" size="sm" onClick={() => setDelOpen(true)} data-testid="delete-job-btn" className="gap-1.5 text-error hover:text-error"><Trash2 className="h-3.5 w-3.5" />Delete</Button>
        </div>
      </div>

      {stages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 p-8 text-center text-sm text-muted-foreground">No stages enabled. Use "Edit stages" to pick which services apply.</div>
      ) : (
        <div className="space-y-5">
          {stages.map((s) => (
            <StageCard key={s} jobId={jobId} stage={s} data={job.stage_data[s]} inflowEnabled={!!job.inflow_enabled}
              pushRecord={job.inflow_pushes?.[s]} isCurrent={s === currentStage} onChanged={refresh} />
          ))}
          <div className="space-y-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 font-display text-sm font-bold text-ink"><NotebookPen className="h-4 w-4 text-muted-foreground" />Miscellaneous · ad-hoc changes</h2>
                <p className="text-xs text-muted-foreground">Internal invoices for changes after final selections — never shown on PDFs or the builder portal.</p>
              </div>
              <AddMiscDialog jobId={jobId} onAdded={refresh} />
            </div>
            {miscKeys.map((mk) => (
              <StageCard key={mk} jobId={jobId} stage={mk} data={job.stage_data?.[mk] || { status: "active", items: [], tax_rate: 10, label: "Miscellaneous" }} inflowEnabled={!!job.inflow_enabled} pushRecord={job.inflow_pushes?.[mk]} isCurrent={false} onChanged={refresh} canDelete={mk !== "misc"} />
            ))}
          </div>
        </div>
      )}

      <JobFiles jobId={jobId} />

      <JobNotes job={job} onSaved={refresh} />

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="delete-job-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Delete this job?</DialogTitle><DialogDescription>"{job.title}" and all its stage selections will be permanently removed. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button onClick={deleteJob} disabled={deleting} data-testid="confirm-delete-job-btn" className="gap-2 bg-error text-white hover:bg-error/90">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Delete job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfViewer
        open={!!pdfView}
        onClose={() => setPdfView(null)}
        blob={pdfView?.blob}
        title={pdfView?.name || "PDF"}
        onDownload={pdfView ? downloadCurrentPdf : undefined}
      />
    </div>
  );
}
